import React, { useId } from 'react';
import type { LucideIcon } from 'lucide-react';

import { Label } from './ui/label';
import { cn } from '../lib/utils';

interface FieldProps {
  label: string;
  /** Render prop receives generated id + aria wiring — spread onto the control. */
  children: (control: {
    id: string;
    'aria-invalid': boolean | undefined;
    'aria-describedby': string | undefined;
    /** True when `required`: announced, not only painted as the hidden asterisk. */
    'aria-required': boolean | undefined;
  }) => React.ReactNode;
  /**
   * Help OR error is shown, never both — error wins. A node, so a help line can
   * carry a resolved value in mono ("Saved as <code>traffic-ops</code>").
   */
  help?: React.ReactNode;
  error?: string | null;
  /**
   * Marks the label with a visual required indicator AND passes
   * aria-required to the control, so the requirement is announced.
   */
  required?: boolean;
  className?: string;
  /**
   * `vertical` (default) is the form rhythm: label over control over message.
   *
   * `horizontal` is for a record laid out as a label/value grid — a detail
   * dialog whose values become editable in place. The Field emits its label
   * and control as DIRECT children of the surrounding grid (`display:
   * contents`), so an editable row lands in the same two columns as the
   * read-only rows above and below it and lines up by construction, rather
   * than by two column templates being kept in sync by hand.
   *
   * The parent owns the columns. It must be a 2-column grid; the message line
   * places itself under the control with `col-start-2`.
   *
   * `choice` is every checkbox or switch that carries a label and help: one
   * bordered row, control first, then the label, then the help beside it. The
   * help is linked by aria-describedby and never sits inside the <label>, so
   * the control's accessible name stays the label alone. A Checkbox is saved
   * with its form; a Switch takes effect when flipped (DESIGN_SYSTEM §5).
   */
  orientation?: 'vertical' | 'horizontal' | 'choice';
  /**
   * Hides the label visually while keeping it for assistive tech. For a
   * control whose meaning is already carried by its surroundings — a name
   * field sitting in a dialog's identity header, where a visible "Name" would
   * be noise. Never a shortcut around writing a label.
   */
  labelHidden?: boolean;
}

/** The required asterisk: painted, never announced (the requirement is announced another way). */
const RequiredMark: React.FC = () => (
  <span aria-hidden="true" className="ml-0.5 text-destructive-emphasis">
    *
  </span>
);

/** The help-or-error line under a control or a group. Error wins, and is announced. */
const FieldMessage: React.FC<{ id: string; help?: React.ReactNode; error?: string | null }> = ({
  id, help, error,
}) =>
  error ? (
    <p id={id} role="alert" className="text-xs text-destructive-emphasis">
      {error}
    </p>
  ) : help ? (
    <p id={id} className="text-xs text-muted-foreground">
      {help}
    </p>
  ) : null;

/**
 * Standard form-field anatomy: Label (always htmlFor-associated) → control →
 * help/error line (12px). Wires aria-invalid, aria-describedby and aria-required so validation
 * is announced, not just painted.
 */
export const Field: React.FC<FieldProps> = ({
  label, children, help, error, required, className, orientation = 'vertical', labelHidden,
}) => {
  const id = useId();
  const messageId = `${id}-message`;
  const hasMessage = Boolean(error || help);

  const renderLabel = (extra?: string) => (
    <Label htmlFor={id} className={cn(labelHidden && 'sr-only', extra)}>
      {label}
      {required ? <RequiredMark /> : null}
    </Label>
  );

  const control = children({
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': hasMessage ? messageId : undefined,
    'aria-required': required ? true : undefined,
  });

  const message = <FieldMessage id={messageId} help={help} error={error} />;

  if (orientation === 'choice') {
    return (
      <div className={cn('flex items-start gap-3 rounded-md border p-3', className)}>
        <div className="flex pt-0.5">{control}</div>
        <div className="min-w-0 flex-1 space-y-1">
          {renderLabel('block cursor-pointer leading-tight')}
          {message}
        </div>
      </div>
    );
  }

  if (orientation === 'horizontal') {
    return (
      // `contents` rather than a wrapper: the label and the control have to be
      // the grid's own children to share its columns with the rows around them.
      <div className={cn('contents', className)}>
        {/* Matches the grid's read-only label column, so an editable row is not
            louder than the rows around it. */}
        {renderLabel('font-normal text-muted-foreground')}
        <div className="space-y-1.5">
          {control}
          {message}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {renderLabel()}
      {control}
      {message}
    </div>
  );
};

export interface FieldGroupProps {
  /** Names the group: it is the fieldset's <legend>, styled like a Label. */
  legend: string;
  /** Explanatory text under the legend, above the controls. Linked by aria-describedby. */
  description?: React.ReactNode;
  /** The help line under the controls. Replaced by `error`, never shown with it. */
  help?: React.ReactNode;
  /** A validation message for the group, announced (role="alert") and linked. */
  error?: string | null;
  /**
   * Paints the asterisk on the legend. A group cannot carry aria-required
   * (it is not an allowed attribute on role=group), so the requirement is
   * announced by the linked validation message when it is not met.
   */
  required?: boolean;
  /** 'stack' (default): one control per line. 'inline': a short fixed row that wraps. */
  orientation?: 'stack' | 'inline';
  /**
   * Controls beside the legend (the wizard's Refresh / Create lens). Rendered
   * outside the <legend>, so their names never join the group's name.
   */
  actions?: React.ReactNode;
  /** Muted text at the end of the legend row (CheckboxList's count). Not a live region. */
  aside?: React.ReactNode;
  /** Keeps the legend for assistive tech only. Never a shortcut around writing one. */
  legendHidden?: boolean;
  /**
   * The concept's glyph before the legend, from the DESIGN_SYSTEM.md §3 icon
   * table (the Device login block's KeyRound). Decorative: it never joins the
   * group's name.
   */
  icon?: LucideIcon;
  /** Base for the legend (`{id}-legend`) and message ids; generated when absent. */
  id?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * Field's counterpart for a group of controls: a native fieldset whose legend
 * names the group, with Field's required asterisk and help/error line.
 *
 * The legend floats, which takes it out of the fieldset border's special
 * rendering so it sits in normal flow beside `actions` and `aside`.
 */
export const FieldGroup: React.FC<FieldGroupProps> = ({
  legend, description, help, error, required, orientation = 'stack', actions, aside,
  legendHidden, icon: Icon, id: idProp, className, children,
}) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const descriptionId = `${id}-description`;
  const messageId = `${id}-message`;
  const describedBy = [description ? descriptionId : null, error || help ? messageId : null]
    .filter(Boolean)
    .join(' ');
  const hasRow = Boolean(actions || aside);

  return (
    <fieldset
      data-field-group=""
      aria-describedby={describedBy || undefined}
      className={cn('m-0 min-w-0 border-0 p-0', className)}
    >
      <legend
        id={`${id}-legend`}
        className={cn(
          legendHidden
            ? 'sr-only'
            : 'float-left flex items-center p-0 text-sm font-medium leading-none',
          !legendHidden && hasRow && 'min-h-7',
        )}
      >
        {Icon && !legendHidden ? (
          <Icon aria-hidden="true" className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : null}
        {legend}
        {required ? <RequiredMark /> : null}
      </legend>
      {hasRow ? (
        <div className="float-right flex min-h-7 items-center gap-2">
          {aside ? <span className="text-xs tabular-nums text-muted-foreground">{aside}</span> : null}
          {actions}
        </div>
      ) : null}
      <div className={cn('clear-both space-y-1.5', (!legendHidden || hasRow) && 'pt-1.5')}>
        {description ? (
          <div id={descriptionId} className="text-xs text-muted-foreground">
            {description}
          </div>
        ) : null}
        <div className={orientation === 'inline' ? 'flex flex-wrap gap-3' : 'space-y-2'}>{children}</div>
        <FieldMessage id={messageId} help={help} error={error} />
      </div>
    </fieldset>
  );
};
